import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { StudentEvent } from "@/services/api/user/events";
import {
  eventDayLayout,
  hasEventEnded,
  localized,
  parseEventDate,
  withMomentLocale,
} from "@/utils/eventFormat";

export type MonthGridDay = {
  day: number;
  dateKey: string;
  monthOffset: -1 | 0 | 1;
  isToday: boolean;
};

interface StudentMonthGridProps {
  year: number;
  month: number;
  selectedDate: string | null;
  eventCounts: Record<string, number>;
  subscribedCounts: Record<string, number>;
  multiDayCounts: Record<string, number>;
  multiDaySubscribedCounts: Record<string, number>;
  events: StudentEvent[];
  language: string;
  onSelectDate: (dateKey: string) => void;
  onChangeMonth: (year: number, month: number) => void;
}

const MAX_RANGE_DAYS = 2000;
/** Maximum event dots shown in one day before the rest become a `+N` count. */
const MAX_DOTS = 3;
/** Bands drawn per week row before extra multi-day runs are summarised by the day panel. */
const MAX_BANDS_PER_ROW = 2;
const BAND_HEIGHT = 4;
const BAND_GAP = 3;
const BAND_BOTTOM = 6;

type BandSegment = {
  id: string;
  title: string;
  subscribed: boolean;
  ended: boolean;
  row: number;
  startCol: number;
  endCol: number;
  startKey: string;
};

type BandRow = { row: number; bands: BandSegment[] };

type GridMetrics = {
  colLeft: number[];
  colWidth: number;
  rowTop: number[];
  rowHeight: number[];
};

const rangeKeys = (startKey: string, endKey: string): string[] => {
  const start = parseEventDate(startKey);
  const end = parseEventDate(endKey);
  if (!start || !end) return [];
  const keys: string[] = [];
  const cursor = start.clone();
  for (
    let step = 0;
    step < MAX_RANGE_DAYS && !cursor.isAfter(end);
    step += 1
  ) {
    keys.push(cursor.format("YYYY-MM-DD"));
    cursor.add(1, "day");
  }
  return keys;
};

const sameNumbers = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const sameMetrics = (a: GridMetrics, b: GridMetrics): boolean =>
  a.colWidth === b.colWidth &&
  sameNumbers(a.colLeft, b.colLeft) &&
  sameNumbers(a.rowTop, b.rowTop) &&
  sameNumbers(a.rowHeight, b.rowHeight);

/**
 * Compact month view: each day shows its date and up to three single-day event
 * dots, with a `+N` marker when there are more. Multi-day runs are drawn as
 * thin continuous bars under the date row so they read as one span instead of
 * a separate dot on every covered day. The selected day's full event cards
 * live in the panel beside the grid.
 */
const StudentMonthGrid: React.FC<StudentMonthGridProps> = ({
  year,
  month,
  selectedDate,
  eventCounts,
  subscribedCounts,
  multiDayCounts,
  multiDaySubscribedCounts,
  events,
  language,
  onSelectDate,
  onChangeMonth,
}) => {
  const { t, i18n } = useTranslation();
  const m = withMomentLocale(i18n.language);

  const monthLabel = m({ year, month }).format("MMMM YYYY");
  const weekdayLabels = useMemo(() => {
    const weekStart = m({ year, month, day: 1 }).startOf("week");
    return Array.from({ length: 7 }, (_, index) =>
      weekStart.clone().add(index, "day").format("ddd"),
    );
  }, [m, i18n.language, year, month]);

  const days = useMemo<MonthGridDay[]>(() => {
    const first = m({ year, month, day: 1 });
    const firstWeekday = first.day();
    const daysInMonth = first.daysInMonth();
    const prevMonth = first.clone().subtract(1, "month");
    const daysInPrevMonth = prevMonth.daysInMonth();
    const todayKey = m().format("YYYY-MM-DD");
    const cells: MonthGridDay[] = [];

    for (let index = firstWeekday - 1; index >= 0; index -= 1) {
      const date = prevMonth.clone().date(daysInPrevMonth - index);
      cells.push({
        day: daysInPrevMonth - index,
        dateKey: date.format("YYYY-MM-DD"),
        monthOffset: -1,
        isToday: date.format("YYYY-MM-DD") === todayKey,
      });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = m({ year, month, day });
      cells.push({
        day,
        dateKey: date.format("YYYY-MM-DD"),
        monthOffset: 0,
        isToday: date.format("YYYY-MM-DD") === todayKey,
      });
    }
    const nextMonth = first.clone().add(1, "month");
    let day = 1;
    while (cells.length < 42) {
      const date = nextMonth.clone().date(day);
      cells.push({
        day,
        dateKey: date.format("YYYY-MM-DD"),
        monthOffset: 1,
        isToday: date.format("YYYY-MM-DD") === todayKey,
      });
      day += 1;
    }
    return cells;
  }, [m, year, month]);

  const visibleDateKeys = useMemo(
    () => new Set(days.map((cell) => cell.dateKey)),
    [days],
  );

  const cellIndexByDateKey = useMemo(() => {
    const positions = new Map<string, number>();
    days.forEach((cell, position) => positions.set(cell.dateKey, position));
    return positions;
  }, [days]);

  const bandSegments = useMemo(() => {
    const segments: BandSegment[] = [];

    events.forEach((event) => {
      const layout = eventDayLayout(event.details);
      if (!layout || layout.kind !== "banner") return;

      const covered = rangeKeys(layout.startKey, layout.endKey).filter((key) =>
        visibleDateKeys.has(key),
      );
      if (covered.length === 0) return;

      const base = {
        id: event.details.id,
        title:
          localized(event.details.title, event.details.title_en, language) ||
          t("Untitled Event"),
        subscribed: Boolean(event.subscribed),
        ended: hasEventEnded(event.details.end_date, event.details.end_time),
      };

      covered.forEach((key) => {
        const position = cellIndexByDateKey.get(key);
        if (position === undefined) return;
        const row = Math.floor(position / 7);
        const col = position % 7;
        const previous = segments[segments.length - 1];
        if (
          previous &&
          previous.id === base.id &&
          previous.row === row &&
          previous.endCol === col - 1
        ) {
          previous.endCol = col;
          return;
        }
        segments.push({
          ...base,
          row,
          startCol: col,
          endCol: col,
          startKey: key,
        });
      });
    });

    return segments;
  }, [events, language, t, visibleDateKeys, cellIndexByDateKey]);

  const bandRows = useMemo<BandRow[]>(() => {
    const byRow = new Map<number, BandSegment[]>();
    bandSegments.forEach((segment) => {
      const list = byRow.get(segment.row);
      if (list) list.push(segment);
      else byRow.set(segment.row, [segment]);
    });

    const rows: BandRow[] = [];
    byRow.forEach((segments, row) => {
      const sorted = [...segments].sort((a, b) => {
        if (a.subscribed !== b.subscribed) return a.subscribed ? -1 : 1;
        if (a.startCol !== b.startCol) return a.startCol - b.startCol;
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
      });
      rows.push({ row, bands: sorted.slice(0, MAX_BANDS_PER_ROW) });
    });
    return rows.sort((a, b) => a.row - b.row);
  }, [bandSegments]);

  const gridRef = useRef<HTMLDivElement | null>(null);
  const [gridMetrics, setGridMetrics] = useState<GridMetrics | null>(null);

  const measureGrid = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const cells = grid.querySelectorAll<HTMLElement>("[data-day-cell]");
    if (cells.length < 7) return;

    const colLeft: number[] = [];
    for (let col = 0; col < 7; col += 1) {
      colLeft.push(cells[col].offsetLeft);
    }
    const colWidth = cells[0].offsetWidth;
    const rowTop: number[] = [];
    const rowHeight: number[] = [];
    for (let row = 0; row * 7 < cells.length; row += 1) {
      const cell = cells[row * 7];
      rowTop.push(cell.offsetTop);
      rowHeight.push(cell.offsetHeight);
    }
    const next: GridMetrics = { colLeft, colWidth, rowTop, rowHeight };
    setGridMetrics((previous) =>
      previous && sameMetrics(previous, next) ? previous : next,
    );
  }, []);

  useEffect(() => {
    measureGrid();
    const grid = gridRef.current;
    if (!grid || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => measureGrid());
    observer.observe(grid);
    return () => observer.disconnect();
  }, [measureGrid, year, month, events, language]);

  const handleMonthChange = (delta: number) => {
    const target = m({ year, month }).add(delta, "month");
    onChangeMonth(target.year(), target.month());
  };

  const handleSelectDay = (cell: MonthGridDay) => {
    if (cell.monthOffset !== 0) {
      const target = m(cell.dateKey, "YYYY-MM-DD");
      onChangeMonth(target.year(), target.month());
    }
    onSelectDate(cell.dateKey);
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={() => handleMonthChange(-1)}
          aria-label={t("Previous month")}
          className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition hover:bg-[#fff3e0] hover:text-[#ffa217]"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="text-base font-bold text-gray-900">{monthLabel}</div>
        <button
          type="button"
          onClick={() => handleMonthChange(1)}
          aria-label={t("Next month")}
          className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition hover:bg-[#fff3e0] hover:text-[#ffa217]"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-1 text-center text-xs font-semibold text-gray-400">
        {weekdayLabels.map((label, index) => (
          <div key={`${label}-${index}`}>{label}</div>
        ))}
      </div>

      <div className="relative">
        <div ref={gridRef} className="grid grid-cols-7 gap-1">
          {days.map((cell, index) => {
            const isSelected = cell.dateKey === selectedDate;
            const totalCount = eventCounts[cell.dateKey] ?? 0;
            const multiDayCount = multiDayCounts[cell.dateKey] ?? 0;
            const singleDayCount = Math.max(0, totalCount - multiDayCount);
            const subscribedCount = subscribedCounts[cell.dateKey] ?? 0;
            const multiDaySubscribed =
              multiDaySubscribedCounts[cell.dateKey] ?? 0;
            const singleDaySubscribed = Math.max(
              0,
              subscribedCount - multiDaySubscribed,
            );
            const dots = Math.min(singleDayCount, MAX_DOTS);
            const subscribedDots = Math.min(singleDaySubscribed, dots);

            return (
              <button
                key={`${cell.dateKey}-${index}`}
                type="button"
                data-day-cell
                onClick={() => handleSelectDay(cell)}
                aria-pressed={isSelected}
                title={
                  totalCount > 0
                    ? t("{{count}} events", { count: totalCount })
                    : undefined
                }
                className={`flex min-h-[60px] min-w-0 flex-col items-center justify-start rounded-xl p-1.5 pb-4 transition sm:min-h-[76px] ${
                  isSelected
                    ? "bg-[#fff8ef] ring-1 ring-[#ffd79a]"
                    : "hover:bg-gray-50"
                }`}
              >
                <span
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-sm ${
                    isSelected
                      ? "bg-[#ffa217] font-bold text-white"
                      : cell.isToday
                        ? "font-bold text-[#ffa217] ring-1 ring-[#ffa217]"
                        : cell.monthOffset !== 0
                          ? "text-gray-300"
                          : "text-gray-800"
                  }`}
                >
                  {cell.day}
                </span>

                {singleDayCount > 0 && (
                  <span className="mt-1 flex h-2 items-center justify-center gap-0.5">
                    {Array.from({ length: dots }).map((_, dotIndex) => (
                      <span
                        key={dotIndex}
                        className={`h-1.5 w-1.5 rounded-full ${
                          dotIndex < subscribedDots
                            ? "bg-[#0DDB47]"
                            : isSelected
                              ? "bg-[#ffa217]"
                              : "bg-[#ffb347]"
                        }`}
                      />
                    ))}
                    {singleDayCount > MAX_DOTS && (
                      <span
                        className={`ml-0.5 text-[9px] font-semibold ${
                          isSelected ? "text-[#b86800]" : "text-gray-400"
                        }`}
                      >
                        +{singleDayCount - MAX_DOTS}
                      </span>
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {gridMetrics && bandRows.length > 0 && (
          <div className="pointer-events-none absolute inset-0 z-10">
            {bandRows.map((bandRow) => {
              const rowTop = gridMetrics.rowTop[bandRow.row];
              const rowHeight = gridMetrics.rowHeight[bandRow.row];
              if (rowTop === undefined || rowHeight === undefined) return null;
              return (
                <React.Fragment key={bandRow.row}>
                  {bandRow.bands.map((band, slot) => {
                    const left = gridMetrics.colLeft[band.startCol];
                    const endLeft = gridMetrics.colLeft[band.endCol];
                    if (left === undefined || endLeft === undefined) return null;
                    return (
                      <button
                        key={`${band.id}-${band.startKey}`}
                        type="button"
                        title={band.title}
                        onClick={() => onSelectDate(band.startKey)}
                        style={{
                          left,
                          top:
                            rowTop +
                            rowHeight -
                            BAND_BOTTOM -
                            BAND_HEIGHT -
                            slot * (BAND_HEIGHT + BAND_GAP),
                          width: endLeft + gridMetrics.colWidth - left,
                          height: BAND_HEIGHT,
                        }}
                        className={`pointer-events-auto absolute rounded-full transition hover:opacity-80 ${
                          band.ended
                            ? "bg-gray-300/60"
                            : band.subscribed
                              ? "bg-[#0DDB47]/60"
                              : "bg-[#ffb347]/55"
                        }`}
                      />
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gray-100 pt-3 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-[#ffb347]" />
          {t("Event")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-1 w-4 rounded-full bg-[#ffb347]/55" />
          {t("Multi-day")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-[#0DDB47]" />
          {t("Subscribed")}
        </span>
      </div>
    </div>
  );
};

export default StudentMonthGrid;
